import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { cn, apiRequest } from '../lib/utils';
import { Link } from 'react-router-dom';

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      text: "Hello! How can we help you today? I can answer questions about our denture services, pricing, or help you schedule a consultation.",
      showCta: true,
      ctaText: "Schedule Your Consultation"
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [idleTimeout, setIdleTimeout] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Reset idle timeout on any interaction
    if (isOpen) {
      if (idleTimeout) clearTimeout(idleTimeout);
      const timeout = setTimeout(() => {
        if (messages.length > 0 && messages[messages.length - 1].type === 'bot') {
          setMessages(prev => [...prev, {
            type: 'bot',
            text: "Have questions about dentures or mouth guards? I can help!",
            showCta: true,
            ctaText: "Learn More"
          }]);
        }
      }, 45000);
      setIdleTimeout(timeout);
    }
    return () => {
      if (idleTimeout) clearTimeout(idleTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, messages]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { type: 'user', text: userMessage }]);
    setIsLoading(true);

    try {
      const response = await apiRequest('/public/chat', {
        method: 'POST',
        body: JSON.stringify({ message: userMessage }),
      });

      setMessages(prev => [...prev, {
        type: 'bot',
        text: response.response,
        showCta: response.show_cta,
        ctaText: response.cta_text
      }]);
    } catch (error) {
      setMessages(prev => [...prev, {
        type: 'bot',
        text: "I apologize, but I'm having trouble connecting right now. Please try again or call us directly at (541) 756-2121.",
        showCta: true,
        ctaText: "Call Us"
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Float button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg",
          isOpen
            ? "bg-slate-800 hover:bg-slate-700"
            : "bg-orange-600 hover:bg-orange-700 hover:scale-110 shadow-orange-600/40 animate-pulse-glow"
        )}
        data-testid="chat-widget-toggle"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
      </button>

      {/* Chat window */}
      <div
        className={cn(
          "fixed bottom-24 right-6 z-50 w-96 max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-2xl overflow-hidden transition-all duration-300",
          isOpen ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-4 scale-95 pointer-events-none"
        )}
        data-testid="chat-widget-window"
      >
        {/* Header */}
        <div className="bg-slate-900 text-white p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-orange-600 flex items-center justify-center">
              <MessageCircle className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold">Community Dental Lab</h3>
              <p className="text-xs text-slate-400">How can we help you today?</p>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="h-80 overflow-y-auto p-4 bg-slate-50">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={cn(
                "mb-4",
                msg.type === 'user' ? "flex justify-end" : "flex justify-start"
              )}
            >
              <div
                className={cn(
                  "max-w-[80%] rounded-2xl px-4 py-3",
                  msg.type === 'user'
                    ? "bg-orange-600 text-white rounded-br-md"
                    : "bg-white text-slate-700 shadow-sm border border-slate-100 rounded-bl-md"
                )}
              >
                <p className="text-sm">{msg.text}</p>
                {msg.showCta && msg.type === 'bot' && (
                  <Link to="/contact">
                    <Button
                      size="sm"
                      className="mt-3 bg-orange-600 hover:bg-orange-700 text-white rounded-full text-xs px-4"
                      data-testid="chat-cta-btn"
                    >
                      <Calendar className="w-3 h-3 mr-2" />
                      {msg.ctaText}
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start mb-4">
              <div className="bg-white text-slate-700 shadow-sm border border-slate-100 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t bg-white">
          <div className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              className="flex-1 rounded-full bg-slate-50 border-slate-200"
              data-testid="chat-input"
            />
            <Button
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              className="w-10 h-10 p-0 rounded-full bg-orange-600 hover:bg-orange-700"
              data-testid="chat-send-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
